import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import { Box } from '@components/Restyle';
import { ProjectChecklist } from '@graphql/generated';
import { ProjectChecklistItem } from '@navigation/projects/ProjectChecklistItem';
import theme from '@themes/theme';

type Props = {
  width?: number;
  listProjectChecklist?: ProjectChecklist[];
  showChecklistCompletedItems: boolean;
  canEdit: boolean;
  onCompletedProjectChecklistItem: (
    item: ProjectChecklist,
    callback?: () => void
  ) => void;
  onSaveProjectChecklistItem: (name: string, item?: ProjectChecklist) => void;
  onDeleteProjectChecklistItem: (itemId: string) => void;
  onReorder: (nextList: ProjectChecklist[]) => void;
};

export const DndKitSortableProjectChecklistListWeb: React.FC<Props> = ({
  width = 500,
  listProjectChecklist,
  showChecklistCompletedItems,
  canEdit,
  onCompletedProjectChecklistItem,
  onSaveProjectChecklistItem,
  onDeleteProjectChecklistItem,
  onReorder,
}) => {
  // Memoized data filtering and sorting
  const data = useMemo(() => {
    const arr = listProjectChecklist || [];
    const completed = arr
      .filter((t) => t.completedAt !== null)
      .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));
    const active = arr
      .filter((t) => t.completedAt === null)
      .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));

    return showChecklistCompletedItems ? [...completed, ...active] : active;
  }, [listProjectChecklist, showChecklistCompletedItems]);

  // State for drag and drop
  const [activeId, setActiveId] = useState<string | null>(null);
  const [selectedItemId, setSelectedItemId] = useState<string | undefined>();
  const [overId, setOverId] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });

  // Configure sensors for drag detection
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 6,
      },
    })
  );

  // Get the currently active item
  const activeItem = useMemo(
    () => data.find((x) => x.id === activeId) || null,
    [activeId, data]
  );

  // Handle drag over to determine drop position
  const handleDragOver = (event: DragOverEvent) => {
    const { over } = event;
    setOverId(over ? String(over.id) : null);
  };

  // Handle drag end to reorder items
  const handleDragEnd = (event: DragEndEvent) => {
    setSelectedItemId?.(undefined);
    setOverId(null);
    setDragOffset({ x: 0, y: 0 });

    const { active, over } = event;
    setActiveId(null);

    if (!over) return;
    if (active.id === over.id) return;

    const oldIndex = data.findIndex((x) => x.id === active.id);
    const newIndex = data.findIndex((x) => x.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const next = arrayMove(data, oldIndex, newIndex);
    onReorder(next);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={(e) => {
        setActiveId(String(e.active.id));
        // Get initial drag position
        if (e.active.rect.current.initial) {
          setDragOffset({
            x: e.active.rect.current.initial.translated?.left || 0,
            y: e.active.rect.current.initial.translated?.top || 0,
          });
        }
      }}
      onDragMove={(e) => {
        // Update drag position in real-time
        if (e.active.rect.current.translated) {
          setDragOffset({
            x: e.active.rect.current.translated.left,
            y: e.active.rect.current.translated.top,
          });
        }
      }}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}>
      <SortableContext
        items={data.map((x) => x.id)}
        strategy={verticalListSortingStrategy}>
        <div style={{ width, position: 'relative' }}>
          {data.map((item, index) => (
            <SortableRow
              key={item.id}
              id={item.id}
              width={width}
              item={item}
              isFirst={index === 0}
              isLast={index === data.length - 1}
              onCompletedProjectChecklistItem={onCompletedProjectChecklistItem}
              onSaveProjectChecklistItem={onSaveProjectChecklistItem}
              onDeleteProjectChecklistItem={onDeleteProjectChecklistItem}
              selectedItemId={selectedItemId}
              setSelectedItemId={setSelectedItemId}
              canEdit={canEdit}
              activeId={activeId}
              overId={overId}
              activeIndex={
                activeId ? data.findIndex((x) => x.id === activeId) : -1
              }
            />
          ))}
        </div>
      </SortableContext>

      {/* Portal-rendered floating item during drag */}
      {activeItem &&
        dragOffset.x !== 0 &&
        dragOffset.y !== 0 &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            style={{
              position: 'fixed',
              zIndex: 9999,
              width,
              pointerEvents: 'none',
              left: 0,
              top: 0,
              transform: `translate3d(${dragOffset.x}px, ${dragOffset.y}px, 0)`,
              opacity: 0.95,
              filter: 'drop-shadow(0 8px 16px rgba(0, 0, 0, 0.15))',
            }}>
            <Box
              style={{
                backgroundColor: theme.colors.greenSecondaryMild,
                borderRadius: theme.borderRadii.xs,
                border: `1px solid ${theme.colors.grey02}`,
                overflow: 'hidden',
              }}>
              <ProjectChecklistItem
                item={activeItem}
                width={width}
                canEdit={canEdit}
                isDragActive={true}
                onCompletedProjectChecklistItem={
                  onCompletedProjectChecklistItem
                }
                onDeleteProjectChecklistItem={onDeleteProjectChecklistItem}
                onSaveProjectChecklistItem={onSaveProjectChecklistItem}
              />
            </Box>
          </div>,
          document.body
        )}
    </DndContext>
  );
};

// Sortable row component for individual checklist items
const SortableRow = ({
  id,
  item,
  width,
  isFirst,
  isLast,
  canEdit,
  onCompletedProjectChecklistItem,
  onSaveProjectChecklistItem,
  onDeleteProjectChecklistItem,
  selectedItemId,
  setSelectedItemId,
  activeId,
  overId,
  activeIndex,
}: {
  id: string;
  item: ProjectChecklist;
  width: number;
  isFirst: boolean;
  isLast: boolean;
  canEdit: boolean;
  onCompletedProjectChecklistItem: (item: ProjectChecklist) => void;
  onSaveProjectChecklistItem: (name: string, item?: ProjectChecklist) => void;
  onDeleteProjectChecklistItem: (itemId: string) => void;
  selectedItemId?: string | undefined;
  setSelectedItemId?: React.Dispatch<React.SetStateAction<string | undefined>>;
  activeId: string | null;
  overId: string | null;
  activeIndex: number;
}) => {
  const { attributes, listeners, setNodeRef, isDragging } = useSortable({ id });

  const isActive = activeId === id;
  const isOver = overId === id;

  // Base styles - all items stay in place
  const style: React.CSSProperties = {
    position: 'relative',
    zIndex: isActive ? 1 : 'auto',
    opacity: 1,
  };

  // Green background for active drag item, matching Task list drag style
  const itemBackgroundStyle: React.CSSProperties =
    isDragging || (isActive && activeId !== null)
      ? {
          backgroundColor: theme.colors.greenSecondaryMild,
          borderRadius: theme.borderRadii.xs,
          transition: 'background-color 0.2s ease',
        }
      : {};

  // Determine if we should show the green dot indicator
  const shouldShowIndicator = () => {
    if (!overId || isActive || activeIndex === -1) return false;

    // Special case: dragging to the top of the list (above the first item)
    // if (isFirst && isOver && index === 0 && activeIndex > 0) {
    //   return 'top';
    // }

    // For all other positions, show indicator at the bottom of the hovered item
    if (isOver) {
      return 'bottom';
    }

    return false;
  };

  const indicatorPosition = shouldShowIndicator();

  // Green dot indicator styles (10px diameter)
  const dotStyle: React.CSSProperties = {
    position: 'absolute',
    width: '10px',
    height: '10px',
    backgroundColor: theme.colors.greenSecondary,
    borderRadius: '50%',
    boxShadow: '0 0 4px rgba(76, 175, 80, 0.5)',
    zIndex: 20,
    top: '-6px',
    left: '0',
  };
  const indicatorStyle: React.CSSProperties = {
    position: 'absolute',
    left: 0,
    right: 0,
    height: '2px',
    backgroundColor: theme.colors.greenSecondary,
    zIndex: 20,
  };
  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        paddingBottom: isLast ? '0' : '8px',
        paddingTop: isFirst ? '0' : '8px',
      }}>
      {/* Top indicator - only shown when dragging to the very top of the list */}
      {indicatorPosition === 'top' && (
        <div style={{ position: 'relative' }}>
          <div style={{ ...indicatorStyle, top: 0 }} />
          <div style={{ ...dotStyle, top: 0 }} />
        </div>
      )}

      <Box
        flexDirection='row'
        alignItems='center'
        width={width}
        // marginBottom={isLast ? 'none' : 'm'}
        style={{
          ...itemBackgroundStyle,
          opacity: isActive ? 0.4 : 1,
          transition: 'opacity 0.2s ease, background-color 0.2s ease',
        }}>
        <ProjectChecklistItem
          item={item}
          width={width}
          canEdit={canEdit}
          isDragActive={isActive}
          onCompletedProjectChecklistItem={onCompletedProjectChecklistItem}
          onDeleteProjectChecklistItem={onDeleteProjectChecklistItem}
          onSaveProjectChecklistItem={onSaveProjectChecklistItem}
          // Only enable drag handles for non-completed, editable items that aren't being dragged
          attributes={
            item.completedAt || !canEdit || isActive ? undefined : attributes
          }
          listeners={
            item.completedAt || !canEdit || isActive ? undefined : listeners
          }
          selectedItemId={selectedItemId}
          setSelectedItemId={setSelectedItemId}
        />
      </Box>

      {/* Bottom indicator - shown for all other drop positions */}
      {indicatorPosition === 'bottom' && (
        <div style={{ position: 'relative' }}>
          <div style={{ ...indicatorStyle, bottom: 0 }} />
          <div style={{ ...dotStyle, bottom: 0 }} />
        </div>
      )}
    </div>
  );
};
